import { useState, useEffect, useCallback, useMemo } from 'react';
import { dashboardApi } from '../services/api/endpoints/dashboard';
import { treasuryApi, type TreasuryForecast, type CashFlow, type TreasurySummary } from '../services/api/endpoints/treasury';
import { ledgerApi } from '../services/api/endpoints/ledger';
import type { TrialBalanceEntry } from '../types/filters';
import { extractAndMapTrialBalanceEntries } from '../utils/trialBalanceMapper';
import { IndexedDBService } from '../services/storage/IndexedDBService';
import { useBusinessUnitStore } from '../stores/businessUnitStore';
import { useFiscalYearStore } from '../stores/fiscalYearStore';

/**
 * Données time series pour les revenus/dépenses
 */
export interface RevenueExpenseTimeSeries {
  date: string;
  revenue: number;
  expenses: number;
  netResult: number;
}

/**
 * Données time series pour la trésorerie
 */
export interface TreasuryTimeSeries {
  date: string;
  totalCash: number;
  inflow: number;
  outflow: number;
  netFlow: number;
}

/**
 * Répartition des charges par catégorie
 */
export interface ExpenseBreakdown {
  name: string;
  value: number;
  color: string;
  percentage: number;
  accountClass: string;
}

/**
 * Répartition des comptes par classe SYSCOHADA
 */
export interface AccountClassBreakdown {
  class: string;
  className: string;
  debitTotal: number;
  creditTotal: number;
  balance: number;
  accountCount: number;
}

/**
 * Données complètes pour les graphiques du dashboard
 */
export interface DashboardTimeSeriesData {
  // Time series
  revenueExpenses: RevenueExpenseTimeSeries[];
  treasury: TreasuryTimeSeries[];
  
  // Breakdown
  expenseBreakdown: ExpenseBreakdown[];
  accountsByClass: AccountClassBreakdown[];
  
  // Prévisions
  forecast: TreasuryForecast | null;
  
  // Résumé
  summary: {
    totalRevenue: number;
    totalExpenses: number;
    netProfit: number;
    profitMargin: number;
    totalCash: number;
    cashChange: number;
  };
  
  // Métadonnées
  period: {
    startDate: string;
    endDate: string;
  };
  lastUpdated: string;
}

/**
 * Couleurs pour les catégories de charges SYSCOHADA
 */
const EXPENSE_COLORS: Record<string, string> = {
  '60': '#ef4444', // Achats
  '61': '#f97316', // Services extérieurs
  '62': '#eab308', // Autres services extérieurs
  '63': '#84cc16', // Impôts, taxes
  '64': '#22c55e', // Charges de personnel
  '65': '#14b8a6', // Autres charges courantes
  '66': '#06b6d4', // Charges financières
  '67': '#3b82f6', // Charges exceptionnelles
  '68': '#8b5cf6', // Dotations amortissements
  '69': '#a855f7', // Participation et impôts sur bénéfices
};

/**
 * Noms des classes SYSCOHADA
 */
const SYSCOHADA_CLASS_NAMES: Record<string, string> = {
  '1': 'Capitaux',
  '2': 'Immobilisations',
  '3': 'Stocks',
  '4': 'Tiers',
  '5': 'Trésorerie',
  '6': 'Charges',
  '7': 'Produits',
  '8': 'Comptes spéciaux',
  '9': 'Engagements'
};

/**
 * Hook pour charger les données time series réelles du dashboard
 * Utilise les endpoints existants: dashboard, treasury, ledger
 * Aucune donnée mockée - production ready
 */
export function useDashboardTimeSeries(period: 'week' | 'month' | 'quarter' | 'year' = 'month') {
  const [data, setData] = useState<DashboardTimeSeriesData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const { effectiveFilter } = useBusinessUnitStore();
  const { currentFiscalYear } = useFiscalYearStore();
  
  // Calculer les dates de période
  const periodDates = useMemo(() => {
    const now = new Date();
    const endDate = now.toISOString().split('T')[0];
    let startDate: string;
    
    switch (period) {
      case 'week':
        const weekAgo = new Date(now);
        weekAgo.setDate(weekAgo.getDate() - 7);
        startDate = weekAgo.toISOString().split('T')[0];
        break;
      case 'month':
        const monthAgo = new Date(now);
        monthAgo.setMonth(monthAgo.getMonth() - 1);
        startDate = monthAgo.toISOString().split('T')[0];
        break;
      case 'quarter':
        const quarterAgo = new Date(now);
        quarterAgo.setMonth(quarterAgo.getMonth() - 3);
        startDate = quarterAgo.toISOString().split('T')[0];
        break;
      case 'year':
        // Utiliser l'exercice fiscal si disponible
        if (currentFiscalYear) {
          startDate = currentFiscalYear.startDate;
        } else {
          const yearAgo = new Date(now);
          yearAgo.setFullYear(yearAgo.getFullYear() - 1);
          startDate = yearAgo.toISOString().split('T')[0];
        }
        break;
    }
    
    return { startDate, endDate };
  }, [period, currentFiscalYear]);
  
  /**
   * Charger les données de revenus depuis l'API dashboard
   */
  const loadRevenueData = async (): Promise<RevenueExpenseTimeSeries[]> => {
    try {
      const response = await dashboardApi.getRevenueData({
        fiscalYearId: currentFiscalYear?.id,
        businessUnitId: effectiveFilter.businessUnitId,
        businessUnitType: effectiveFilter.businessUnitType,
        startDate: periodDates.startDate,
        endDate: periodDates.endDate,
        period: period === 'week' ? 'day' : period === 'month' ? 'week' : 'month'
      });
      
      if (response.success && response.data && Array.isArray(response.data)) {
        return response.data.map((item: any) => ({
          date: item.date || item.month || item.period,
          revenue: item.revenue || item.amount || 0,
          expenses: item.expenses || 0,
          netResult: (item.revenue || item.amount || 0) - (item.expenses || 0)
        }));
      }
    } catch (err) {
      console.warn('Could not load revenue data from API:', err);
    }
    
    return [];
  };
  
  /**
   * Charger les données de dépenses depuis l'API dashboard
   */
  const loadExpensesData = async (): Promise<ExpenseBreakdown[]> => {
    try {
      const response = await dashboardApi.getExpensesData({
        fiscalYearId: currentFiscalYear?.id,
        businessUnitId: effectiveFilter.businessUnitId,
        businessUnitType: effectiveFilter.businessUnitType,
        startDate: periodDates.startDate,
        endDate: periodDates.endDate
      });
      
      if (response.success && response.data && Array.isArray(response.data)) {
        return response.data.map((item: any) => ({
          name: item.name || item.category || 'Autres',
          value: item.value || item.amount || 0,
          color: item.color || EXPENSE_COLORS[item.accountClass?.substring(0, 2)] || '#94a3b8',
          percentage: item.percentage || 0,
          accountClass: item.accountClass || ''
        }));
      }
    } catch (err) {
      console.warn('Could not load expenses breakdown from API:', err);
    }
    
    return [];
  };
  
  /**
   * Charger les prévisions de trésorerie depuis l'API treasury
   */
  const loadTreasuryForecast = async (): Promise<TreasuryForecast | null> => {
    try {
      const forecastPeriod = period === 'week' ? '7d' : period === 'month' ? '30d' : '90d';
      
      const response = await treasuryApi.getForecast({
        forecastPeriod,
        businessUnitId: effectiveFilter.businessUnitId,
        businessUnitType: effectiveFilter.businessUnitType
      });
      
      if (response.success && response.data) {
        return response.data;
      }
    } catch (err) {
      console.warn('Could not load treasury forecast from API:', err);
    }
    
    return null;
  };
  
  /**
   * Charger les flux de trésorerie depuis l'API treasury
   */
  const loadCashFlows = async (): Promise<TreasuryTimeSeries[]> => {
    try {
      const response = await treasuryApi.getCashFlows({
        startDate: periodDates.startDate,
        endDate: periodDates.endDate,
        businessUnitId: effectiveFilter.businessUnitId,
        businessUnitType: effectiveFilter.businessUnitType
      });
      
      if (response.success && response.data && Array.isArray(response.data)) {
        // Grouper les flux par date
        const flowsByDate: Map<string, { inflow: number; outflow: number }> = new Map();
        
        response.data.forEach((flow: CashFlow) => {
          const date = flow.date.split('T')[0];
          const existing = flowsByDate.get(date) || { inflow: 0, outflow: 0 };
          
          if (flow.type === 'inflow') {
            existing.inflow += flow.amount;
          } else {
            existing.outflow += flow.amount;
          }
          
          flowsByDate.set(date, existing);
        });
        
        // Convertir en tableau trié
        const sortedDates = Array.from(flowsByDate.keys()).sort();
        let runningBalance = 0;
        
        return sortedDates.map(date => {
          const flows = flowsByDate.get(date)!;
          const netFlow = flows.inflow - flows.outflow;
          runningBalance += netFlow;
          
          return {
            date,
            totalCash: runningBalance,
            inflow: flows.inflow,
            outflow: flows.outflow,
            netFlow
          };
        });
      }
    } catch (err) {
      console.warn('Could not load cash flows from API:', err);
    }
    
    return [];
  };
  
  /**
   * Charger le résumé de trésorerie depuis l'API
   */
  const loadTreasurySummary = async (): Promise<TreasurySummary | null> => {
    try {
      const response = await treasuryApi.getSummary({
        fiscalYearId: currentFiscalYear?.id,
        businessUnitId: effectiveFilter.businessUnitId,
        businessUnitType: effectiveFilter.businessUnitType,
        startDate: periodDates.startDate,
        endDate: periodDates.endDate
      });
      
      if (response.success && response.data) {
        return response.data;
      }
    } catch (err) {
      console.warn('Could not load treasury summary from API:', err);
    }
    
    return null;
  };
  
  /**
   * Charger la balance des comptes depuis l'API ledger
   * pour le breakdown par classe SYSCOHADA
   */
  const loadTrialBalance = async (): Promise<AccountClassBreakdown[]> => {
    try {
      const response = await ledgerApi.getTrialBalance({
        fiscalYearId: currentFiscalYear?.id,
        businessUnitId: effectiveFilter.businessUnitId,
        businessUnitType: effectiveFilter.businessUnitType
      });
      
      if (response.success && response.data) {
        // Mapper les données de l'API vers le format frontend
        const mappedEntries = extractAndMapTrialBalanceEntries(response.data);
        
        // Grouper par classe
        const byClass: Map<string, AccountClassBreakdown> = new Map();
        
        mappedEntries.forEach((entry: TrialBalanceEntry) => {
          const classCode = entry.account?.code?.charAt(0) || '0';
          
          const existing = byClass.get(classCode) || {
            class: classCode,
            className: SYSCOHADA_CLASS_NAMES[classCode] || `Classe ${classCode}`,
            debitTotal: 0,
            creditTotal: 0,
            balance: 0,
            accountCount: 0
          };
          
          existing.debitTotal += entry.debit || 0;
          existing.creditTotal += entry.credit || 0;
          existing.balance += entry.balance || 0;
          existing.accountCount += 1;
          
          byClass.set(classCode, existing);
        });
        
        // Convertir en tableau trié
        return Array.from(byClass.values()).sort((a, b) => a.class.localeCompare(b.class));
      }
    } catch (err) {
      console.warn('Could not load trial balance from API:', err);
    }
    
    return [];
  };
  
  /**
   * Charger toutes les données
   */
  const loadAllData = useCallback(async () => {
    if (!navigator.onLine) {
      // Essayer de charger depuis le cache
      const cached = await IndexedDBService.get<DashboardTimeSeriesData>(
        'dashboard_timeseries',
        `${period}_${effectiveFilter.businessUnitId || 'all'}`
      );
      
      if (cached) {
        setData(cached);
        setLoading(false);
        return;
      }
      
      setError('Hors ligne - Données en cache non disponibles');
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      // Charger toutes les données en parallèle
      const [
        revenueExpenses,
        expenseBreakdown,
        treasuryTimeSeries,
        forecast,
        treasurySummary,
        accountsByClass
      ] = await Promise.all([
        loadRevenueData(),
        loadExpensesData(),
        loadCashFlows(),
        loadTreasuryForecast(),
        loadTreasurySummary(),
        loadTrialBalance()
      ]);
      
      // Utiliser les prévisions pour compléter les données de trésorerie si disponibles
      let treasury = treasuryTimeSeries;
      
      if (forecast?.dailyProjections && forecast.dailyProjections.length > 0 && treasury.length === 0) {
        treasury = forecast.dailyProjections.map(proj => ({
          date: proj.date,
          totalCash: proj.projectedBalance,
          inflow: proj.expectedInflow,
          outflow: proj.expectedOutflow,
          netFlow: proj.expectedInflow - proj.expectedOutflow
        }));
      }
      
      // Calculer le résumé
      const totalRevenue = revenueExpenses.reduce((sum, item) => sum + item.revenue, 0);
      const totalExpenses = expenseBreakdown.reduce((sum, item) => sum + item.value, 0) || 
                           revenueExpenses.reduce((sum, item) => sum + item.expenses, 0);
      
      const firstCash = treasury[0]?.totalCash ?? treasurySummary?.totalBalance ?? 0;
      const lastCash = treasury[treasury.length - 1]?.totalCash ?? treasurySummary?.totalBalance ?? 0;
      
      const result: DashboardTimeSeriesData = {
        revenueExpenses,
        treasury,
        expenseBreakdown,
        accountsByClass,
        forecast,
        summary: {
          totalRevenue,
          totalExpenses,
          netProfit: totalRevenue - totalExpenses,
          profitMargin: totalRevenue > 0 ? ((totalRevenue - totalExpenses) / totalRevenue) * 100 : 0,
          totalCash: lastCash,
          cashChange: lastCash - firstCash
        },
        period: periodDates,
        lastUpdated: new Date().toISOString()
      };
      
      setData(result);
      
      // Mettre en cache
      await IndexedDBService.update('dashboard_timeseries', {
        id: `${period}_${effectiveFilter.businessUnitId || 'all'}`,
        ...result
      });
      
    } catch (err) {
      console.error('Error loading dashboard time series data:', err);
      setError('Impossible de charger les données du dashboard');
    } finally {
      setLoading(false);
    }
  }, [period, effectiveFilter.businessUnitId, effectiveFilter.businessUnitType, currentFiscalYear?.id, periodDates]);
  
  // Charger les données au montage et quand les filtres changent
  useEffect(() => {
    loadAllData();
  }, [loadAllData]);
  
  return {
    data,
    loading,
    error,
    refresh: loadAllData,
    period,
    periodDates
  };
}
